/**
 * Helpers da tela Impostos INSS (Parametrizações) —
 * alinhados ao protótipo `definicao/esocial-imposto.txt`.
 */

export function formatarCnpj(cnpj) {
  const d = String(cnpj ?? "").replace(/\D/g, "");
  if (!/^\d{14}$/.test(d)) return String(cnpj ?? "");
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

export function formatarPct(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return String(valor ?? "");
  return `${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function formatarPctInput(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Extrai CNPJ 14 dígitos se presente em id / cnpj / detalhes. */
export function extrairCnpjDoCombo(empresa) {
  if (!empresa) return "";
  const fromId = String(empresa.id ?? "").replace(/\D/g, "");
  if (/^\d{14}$/.test(fromId)) return fromId;
  const fromField = String(empresa.cnpj ?? "").replace(/\D/g, "");
  if (/^\d{14}$/.test(fromField)) return fromField;
  const m = String(empresa.detalhes ?? "").match(
    /(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}|\d{14})/
  );
  return m ? String(m[0]).replace(/\D/g, "") : "";
}

/** id_empresa numérico do TableCard (quando não é CNPJ). */
export function extrairIdEmpresaDoCombo(empresa) {
  if (!empresa) return null;
  const raw = empresa.id ?? empresa.id_empresa;
  if (raw == null || raw === "") return null;
  const digits = String(raw).replace(/\D/g, "");
  if (/^\d{14}$/.test(digits)) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function razaoSocialDoCombo(empresa) {
  if (!empresa) return "";
  const detalhes = String(empresa.detalhes ?? "").trim();
  if (!detalhes) return String(empresa.razaoSocial ?? empresa.razao_social ?? "");
  const semCnpj = detalhes
    .replace(/\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}/g, "")
    .replace(/\d{14}/g, "")
    .replace(/[-–|/]+/g, " ")
    .trim();
  return semCnpj || detalhes;
}

/**
 * Percentual em pt-BR (vírgula decimal, ex. "5,80") ou número/string com ponto.
 */
export function parsePercentual(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalizado = String(value)
    .trim()
    .replace(/\s/g, "")
    .replace("%", "")
    .replace(",", ".");
  if (normalizado === "") return null;
  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : null;
}

/** Converte os três campos de alíquota para número, ou `null` se algum for inválido. */
export function validarAliquotas({ terceiros, rat, inss }) {
  const terceirosVal = parsePercentual(terceiros);
  const ratVal = parsePercentual(rat);
  const inssVal = parsePercentual(inss);
  if (terceirosVal == null || ratVal == null || inssVal == null) return null;
  return { terceiros: terceirosVal, rat: ratVal, inss: inssVal };
}

/** Chave numérica aaaamm para ordenação (a partir de mm/aaaa ou YYYY-MM). */
export function parseReferenciaKey(ref) {
  const s = String(ref || "").trim();
  const mmAaaa = s.match(/^(\d{1,2})\/(\d{4})$/);
  if (mmAaaa) {
    const mes = parseInt(mmAaaa[1], 10);
    const ano = parseInt(mmAaaa[2], 10);
    if (mes < 1 || mes > 12) return null;
    return ano * 12 + mes;
  }
  const iso = s.match(/^(\d{4})-(\d{2})$/);
  if (iso) {
    const ano = parseInt(iso[1], 10);
    const mes = parseInt(iso[2], 10);
    if (mes < 1 || mes > 12) return null;
    return ano * 12 + mes;
  }
  return null;
}

/** Converte mm/aaaa → YYYY-MM (ou null se inválido). */
export function mmAaaaParaIso(ref) {
  const s = String(ref || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const mes = parseInt(m[1], 10);
  const ano = parseInt(m[2], 10);
  if (mes < 1 || mes > 12) return null;
  return `${ano}-${String(mes).padStart(2, "0")}`;
}

/** Converte YYYY-MM → mm/aaaa. */
export function isoParaMmAaaa(iso) {
  const s = String(iso || "").trim().slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return String(iso || "");
  return `${m[2]}/${m[1]}`;
}

/** Máscara digitação mm/aaaa. */
export function mascararMmAaaa(raw) {
  const v = String(raw || "")
    .replace(/\D/g, "")
    .slice(0, 6);
  if (v.length >= 3) return `${v.slice(0, 2)}/${v.slice(2)}`;
  return v;
}

export function ordenarImpostos(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ca = String(a.cnpj || "").localeCompare(String(b.cnpj || ""));
    if (ca !== 0) return ca;
    const ra = parseReferenciaKey(a.referencia) || 0;
    const rb = parseReferenciaKey(b.referencia) || 0;
    return rb - ra;
  });
}

/**
 * Filtra linhas no cliente (protótipo).
 * @param {object[]} impostos
 * @param {{ cnpj?: string, qEmpresa?: string, refIni?: string, refFim?: string, cprb?: string }} filtros
 *   refIni/refFim em mm/aaaa
 */
export function filtrarImpostosClient(impostos, filtros = {}) {
  const cnpjSel = String(filtros.cnpj || "").replace(/\D/g, "");
  const q = String(filtros.qEmpresa || "")
    .trim()
    .toLowerCase();
  const d = String(filtros.qEmpresa || "").replace(/\D/g, "");
  const refIni = parseReferenciaKey(filtros.refIni);
  const refFim = parseReferenciaKey(filtros.refFim);
  const cprb = filtros.cprb || "";

  return (impostos || []).filter((r) => {
    const cnpj = String(r.cnpj || "").replace(/\D/g, "");
    const razao = String(r.razaoSocial || "").toLowerCase();
    if (cnpjSel) {
      if (cnpj !== cnpjSel) return false;
    } else if (q || d.length >= 2) {
      const matchCnpj = d.length >= 2 && cnpj.includes(d);
      const matchNome = q.length >= 2 && razao.includes(q);
      if (!matchCnpj && !matchNome) return false;
    }
    const rk = parseReferenciaKey(r.referenciaDisplay || isoParaMmAaaa(r.referencia));
    if (refIni != null && (rk == null || rk < refIni)) return false;
    if (refFim != null && (rk == null || rk > refFim)) return false;
    const cprbVal = r.cprb === "Sim" ? "Sim" : "Não";
    if (cprb && cprbVal !== cprb) return false;
    return true;
  });
}
